import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Button,
  Spinner,
  Form,
  Modal,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { Check2, X } from 'react-bootstrap-icons';
import { AuthContext } from 'api/context';
import { LeadContext } from 'Leads/Details/hooks/context';
import { number } from 'mathjs';
import { getStateSnowLoss } from '../system-losses';

export const SystemLosses = ({ setPVwattsLosses, initialLosses = null }) => {
  const { company: id, user, newEndpoint } = useContext(AuthContext);
  const { lead } = useContext(LeadContext);

  const [showLossesModal, setShowLossesModal] = useState(false);
  const handleClose = () => setShowLossesModal(false);

  const endpoint = newEndpoint('hardware/losses/');

  const normalizeLosses = (src) => {
    const obj = src && typeof src === 'object' ? src : {};
    const pick = (...keys) => {
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
      }
      return undefined;
    };
    const toNum = (v, fallback = 0) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    };
    return {
      soiling: toNum(pick('soiling', 'Soiling'), 2),
      shading: toNum(pick('shading', 'Shading'), 0),
      snow: toNum(pick('snow', 'Snow'), 0),
      mismatch: toNum(pick('mismatch', 'Mismatch'), 1.5),
      wiring: toNum(pick('wiring', 'Wiring'), 2),
      connections: toNum(pick('connections', 'Connections'), 0.5),
      lid: toNum(
        pick('lid', 'Light_Induced_Degradation', 'light_induced_degradation'),
        1.5
      ),
      nameplate_rating: toNum(
        pick('nameplate_rating', 'Nameplate_Rating', 'nameplateRating'),
        1
      ),
      age: toNum(pick('age', 'Age'), 0),
      availability: toNum(pick('availability', 'Availability'), 1),
    };
  };

  const [losses, setLosses] = useState(normalizeLosses(initialLosses));
  const [snow, setSnow] = useState(normalizeLosses(initialLosses).snow);
  
  useEffect(() => {
    if (!initialLosses || typeof initialLosses !== 'object') return;
    const norm = normalizeLosses(initialLosses);
    setLosses((prev) => ({ ...(prev || {}), ...norm }));
    if (norm.snow !== undefined && norm.snow !== null) setSnow(norm.snow);
  }, [initialLosses]);
  
  useEffect(() => {
    if (lead?.property?.state) {
      const snowLoss = getStateSnowLoss(lead?.property?.state);
      if (snowLoss !== null && snowLoss !== undefined) {
        const snowVal = parseFloat(snowLoss);
        setSnow(snowVal);
        setLosses((prev) => ({ ...(prev || {}), snow: snowVal }));
      }
    }
    
    endpoint
      .retrieve(id)
      .then((data) => {
        const base = normalizeLosses(data);
        setLosses((prev) => ({ ...base, ...(prev || {}) }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetLosses = () => {
    setPVwattsLosses(losses);
    handleClose();
  };

  const handleUpdateField = (value, field) => {
    const num = parseFloat(value);
    const next = Number.isFinite(num) ? num : 0;
    if (field === 'snow') setSnow(next);
    setLosses((prev) => ({ ...(prev || {}), [field]: next }));
  };

  return (
    <>
      <Button
        variant="outline-dark"
        size="sm"
        onClick={() => setShowLossesModal(true)}
      >
            System Losses
          </Button>

          <Modal show={showLossesModal} onHide={handleClose}>
            <Modal.Header closeButton>
              <Modal.Title>System Losses</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Soiling</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).soiling ?? 0}
                      onChange={(e) => handleUpdateField(e.target.value, 'soiling')}
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Shading</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).shading ?? 0}
                      onChange={(e) => handleUpdateField(e.target.value, 'shading')}
                      step="0.10"
                      min="0.00"
                      disabled
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Snow</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={snow}
                      onChange={(e) => handleUpdateField(e.target.value, 'snow')}
                      step="0.10"
                      min="0.00"
                      disabled
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Mismatch</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).mismatch ?? 0}
                      onChange={(e) => handleUpdateField(e.target.value, 'mismatch')}
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Wiring</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).wiring ?? 0}
                      onChange={(e) => handleUpdateField(e.target.value, 'wiring')}
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Connections</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).connections ?? 0}
                      onChange={(e) =>
                        handleUpdateField(e.target.value, 'connections')
                      }
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                  <InputGroup.Text>Light Induced Degradation</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).lid ?? 0}
                      onChange={(e) => handleUpdateField(e.target.value, 'lid')}
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Nameplate Rating</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).nameplate_rating ?? 0}
                      onChange={(e) =>
                        handleUpdateField(e.target.value, 'nameplate_rating')
                      }
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Age</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).age ?? 0}
                      onChange={(e) => handleUpdateField(e.target.value, 'age')}
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
              <Row className="my-1">
                <Col>
                  <InputGroup>
                    <InputGroup.Prepend>
                      <InputGroup.Text>Availability</InputGroup.Text>
                    </InputGroup.Prepend>
                    <Form.Control
                      type="number"
                      value={(losses || {}).availability ?? 0}
                      onChange={(e) =>
                        handleUpdateField(e.target.value, 'availability')
                      }
                      step="0.10"
                      min="0.00"
                      required
                      disabled={user.role !== 'admin'}
                    />
                    <InputGroup.Append>
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup.Append>
                  </InputGroup>
                </Col>
              </Row>
            </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
              </Button>
              <Button variant="primary" onClick={handleSetLosses}>
                Set System Losses
              </Button>
            </Modal.Footer>
          </Modal>
    </>
  );
};

SystemLosses.propTypes = {
  setPVwattsLosses: PropTypes.func.isRequired,
  initialLosses: PropTypes.object,
};

const Shading = ({
  shaded,
  colorRange,
  shadingHandler,
  weatherDataset,
  setWeatherDataset,
  weatherDatasetFetchedPath,
  weatherDatasetReady,
  onWeatherDatasetFetched,
  showOverlay,
  toggleOverlay,
  shadingMonth,
  shadingStatus,
  shadingIsRunning,
  serverSideShadingIsRunning,
}) => {
  const { newEndpoint } = useContext(AuthContext);
  const { lead, designId, CanvasAPI } = useContext(LeadContext);
  const activeDesignId =
    (typeof designId === 'string' && designId.trim()) ||
    CanvasAPI?.getCanvasTargetId?.() ||
    '';
  // const [init, setInit] = useState(true);
  // const [loading, setLoading] = useState();
  // const [success, setSuccess] = useState(false);
  const [serverSide, setServerSide] = useState();
  const [fetchingDataset, setFetchingDataset] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [cmin, cmax] = colorRange;

  const [datasetsMeta, setDatasetsMeta] = useState(null);
  useEffect(() => {
    try {
      const api = newEndpoint('api/nsrdb/datasets/');
      api
        .list()
        .then((data) => setDatasetsMeta(data))
        .catch(() => setDatasetsMeta(null));
    } catch {
      setDatasetsMeta(null);
    }
  }, []);

  const choices = Array.isArray(datasetsMeta?.keys)
    ? datasetsMeta.keys
    : [];
  const labels = datasetsMeta?.datasets || {};
  const defaultDataset = datasetsMeta?.default || 'nsrdb_goes_v4_2024';

  const resolveLabel = (key) => {
    if (labels?.[key]?.label) return labels[key].label;
    return key;
  };

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'June',
    'July',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
    'Year',
  ];
  
  // useEffect(() => {
  //   if (shadingStatus === 1) {
  //     setLoading(false);
  //     setSuccess(true);
  //     setInit(false);
  //   } else if (shadingStatus === 0) {
  //     setLoading(false);
  //     setSuccess(true);
  //   } else if (shadingStatus === -1) {
  //     setLoading(false);
  //     setSuccess(false);
  //   }
  // }, [shadingStatus, shadingMonth]);

  const isLoading = shadingStatus === 1;
  const isCompleted = shadingStatus === 0;
  const isError = shadingStatus === -1;

  const pollDatasetStatus = async (taskId, datasetKey) => {
    const api = newEndpoint('api/nsrdb/status/');
    const start = Date.now();
    const timeoutMs = 120000;
    let attempt = 0;

    while (Date.now() - start < timeoutMs) {
      attempt += 1;
      const params = new URLSearchParams();
      if (taskId) {
        params.set('task_id', taskId);
      } else {
        params.set('uid', lead?.uid || '');
        params.set('dataset', datasetKey);
      }
      if (activeDesignId) params.set('design_id', activeDesignId);
      const query = `?${params.toString()}`;

      const res = await api.list(query);
      if (res?.status === 'ready' && res?.weather_dataset) {
        if (typeof onWeatherDatasetFetched === 'function') {
          onWeatherDatasetFetched(res.weather_dataset);
        }
        return res.weather_dataset;
      }
      if (res?.status === 'failed' || res?.success === false) {
        throw new Error(res?.error || 'Failed to fetch dataset');
      }

      const delayMs = Math.min(1000 + attempt * 500, 5000);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    throw new Error('Timed out waiting for dataset');
  };

  const fetchDataset = async () => {
    if (fetchingDataset) return null;
    setFetchError(null);
    setFetchingDataset(true);
    try {
      const api = newEndpoint('api/nsrdb/fetch/');
      if (!lead?.uid) {
        throw new Error('Lead is required to fetch dataset');
      }
      const datasetKey = weatherDataset || defaultDataset;
      const payload = { uid: lead?.uid, dataset: datasetKey };
      if (activeDesignId) payload.design_id = activeDesignId;
      const res = await api.create(payload);
      if (!res || res.success === false) {
        throw new Error(res?.error || 'Failed to fetch dataset');
      }
      if (res?.weather_dataset) {
        if (typeof onWeatherDatasetFetched === 'function') {
          onWeatherDatasetFetched(res.weather_dataset);
        }
        if (res?.status === 'ready' || !res?.status)
          return res.weather_dataset;
      }
      if (res?.status === 'pending') {
        return (await pollDatasetStatus(res.task_id, datasetKey)) || null;
      }
      return null;
    } catch (e) {
      setFetchError(e?.message || 'Failed to fetch dataset');
      return null;
    } finally {
      setFetchingDataset(false);
    }
  };

  const runShading = async () => {
    if (
      isLoading ||
      fetchingDataset ||
      shadingIsRunning ||
      serverSideShadingIsRunning
    )
      return;
    const selectedDatasetKey = weatherDataset || defaultDataset;
    const expectedFilename = `${selectedDatasetKey}.csv`;
    const currentFilename = weatherDatasetFetchedPath
      ? (String(weatherDatasetFetchedPath).split('/').pop() || '').trim()
      : null;
    let datasetPath =
      currentFilename === expectedFilename ? weatherDatasetFetchedPath : null;
    if (!datasetPath || !weatherDatasetReady) {
      datasetPath = await fetchDataset();
      if (!datasetPath) return;
    }
    // setLoading(true);
    // send shading request
    await shadingHandler(serverSide, true, datasetPath);
    // .then((status) => {
    //   setSuccess(status);
    //   setLoading(false);
    // });
  };

  return (
    <>
      <Row className="mt-2">
        <Col>
          <Form.Group>
            <Form.Label className="mb-1">Weather dataset</Form.Label>
            <InputGroup>
              <Form.Control
                size="sm"
                as="select"
                value={weatherDataset || defaultDataset}
                onChange={(e) =>
                  typeof setWeatherDataset === 'function'
                    ? setWeatherDataset(e.target.value)
                    : null
                }
              >
                {(choices.length ? choices : [defaultDataset]).map((k) => (
                  <option key={k} value={k}>
                    {resolveLabel(k)}
                  </option>
                ))}
              </Form.Control>
            </InputGroup>
            <Form.Text className="text-muted">
              {weatherDatasetReady
                ? `Dataset ready: ${weatherDataset || defaultDataset}`
                : 'Dataset will be fetched automatically when running shading.'}
            </Form.Text>
            {fetchError && (
              <Form.Text className="text-danger">{fetchError}</Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row xs={1} md={1}>
        <Col>
          <Form.Check
            id="chk-server-side"
            type="switch"
            checked={serverSide}
            onChange={(e) => setServerSide(e.target.checked)}
            label="Server-Side Shading"
          />
        </Col>
      </Row>
      <Row xs={1} md={1}>
        <Col>
          <Button
            id="shading-btn"
            className="w-100"
            onClick={runShading}
            disabled={
              !lead?.uid ||
              isLoading ||
              fetchingDataset ||
              shadingIsRunning ||
              serverSideShadingIsRunning
            }
          >
            {fetchingDataset
              ? 'Fetching Dataset…'
              : shadingIsRunning || serverSideShadingIsRunning || isLoading
                ? 'Running…'
                : 'Run Shading'}
            {(isLoading || shadingIsRunning || serverSideShadingIsRunning) && (
              <Spinner animation="border" role="status" size="sm" />
            )}
            {isLoading &&
              `(${shadingStatus in monthNames ? monthNames[shadingMonth] : 'Jan'})`}
            {isCompleted && !shadingIsRunning && !serverSideShadingIsRunning && (
              <Check2 size="24px" />
            )}
            {isError && <X color="red" size="24px" />}
          </Button>
        </Col>
        {/* <Col>
          <Button
            className="w-100"
            onClick={toggleOverlay}
            disabled={!shaded}
          // disabled={!accordion || !konvaKeys.includes(accordion)}
          >
            {`${showOverlay ? 'Hide' : 'Show'} Production`}
            {/* <CloudSun size={24} />
          </Button>
        </Col> */}
        {/* <Col className="my-2">
          Shading Available
          {shaded ? <Check2 color="green" size={24} /> : <X color="red" size={24} />}
        </Col> */}
      </Row>
      {shadingStatus !== -1 && (
        <Row className="mt-3">
          <Col>
            <strong>
              Insolation (KWh/m
              <sup>2</sup>
              /year)
            </strong>
            <img
              src="/cmap_inferno.png"
              width="100%"
              height={30}
              alt="legend"
            />
            <div className="w-100 px-1 text-primary d-flex justify-content-between border border-primary border-bottom-0">
              <p className="m-0">{cmin}</p>
              <p className="m-0">{cmax}</p>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

Shading.propTypes = {
  shaded: PropTypes.bool.isRequired,
  colorRange: PropTypes.arrayOf(PropTypes.number).isRequired,
  shadingHandler: PropTypes.func.isRequired,
  weatherDataset: PropTypes.string,
  setWeatherDataset: PropTypes.func,
  weatherDatasetFetchedPath: PropTypes.string,
  weatherDatasetReady: PropTypes.bool,
  onWeatherDatasetFetched: PropTypes.func,
  showOverlay: PropTypes.bool.isRequired,
  toggleOverlay: PropTypes.func.isRequired,
  shadingMonth: PropTypes.number,
  shadingStatus: PropTypes.number,
  shadingIsRunning: PropTypes.bool,
  serverSideShadingIsRunning: PropTypes.bool,
};

export default Shading;
